import { useState } from 'react';
import { useAlertConfigHistory } from './useAlertConfig';
import { Pager } from '../../components/Pager';
import type { AlertConfigHistoryItem } from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 6 — alert_config_history 타임라인.
// PATCH 성공 시 useAlertConfig 훅의 invalidate 가 본 쿼리 키 프리픽스(`alert-config-history`) 를
// 날려 자동 refetch 된다.

const PAGE_SIZE = 20;

function fmtTs(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

function summarize(item: AlertConfigHistoryItem): string {
  if (item.note && item.note.trim()) return item.note;
  // note 가 없으면 before/after 변화 요약.
  if (item.scope === 'recipient') return '수신자 변경';
  if (item.afterValue && typeof item.afterValue === 'object') {
    return `${item.scope} 변경`;
  }
  return `${item.scope} 변경`;
}

export function HistoryTimeline(): JSX.Element {
  const [page, setPage] = useState(0);
  const q = useAlertConfigHistory(page, PAGE_SIZE);

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;

  return (
    <section className="as-card" data-testid="as-history-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">6. 변경 이력</h3>
          <div className="as-section__sub">
            alert_config_history — 최근 변경 순. PATCH / 수신자 CRUD 성공 시 자동
            기록됩니다.
          </div>
        </div>
      </div>

      {q.isLoading && <div className="as-info">로딩 중…</div>}
      {q.isError && (
        <div className="as-warn">이력 로드 실패: {(q.error as Error).message}</div>
      )}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <div className="as-info">아직 변경 이력이 없습니다.</div>
      )}

      {items.length > 0 && (
        <div className="as-timeline">
          {items.map((h) => (
            <div key={h.changeId} className="as-timeline__item">
              <div className="as-timeline__ts">{fmtTs(h.changedAt)}</div>
              <div className="as-timeline__body">
                <span className="as-timeline__scope">
                  {h.scope}
                  {h.changedBy ? ` · ${h.changedBy}` : ''}
                </span>
                <span className="as-timeline__note">{summarize(h)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
    </section>
  );
}
